import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, Shield } from 'lucide-react';
import { Button } from '@/components/ui/Button';

const COOKIE_CONSENT_KEY = 'five_ai_cookie_consent';
const CONSENT_VERSION = '1.0';

interface CookieConsent {
  accepted: boolean;
  timestamp: string;
  version: string;
  preferences?: {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
  };
}

function getStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.accepted && parsed.version === CONSENT_VERSION) return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveConsent(preferences: { essential: boolean; analytics: boolean; marketing: boolean }): void {
  const consent: CookieConsent = {
    accepted: true,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
    preferences,
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
}

export default function CookieConsentBanner() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const isOnboarding = location.pathname.startsWith('/onboarding');

  const handleAcceptAll = useCallback(() => {
    saveConsent({ essential: true, analytics: true, marketing: true });
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    saveConsent({ essential: true, analytics, marketing });
    setVisible(false);
  }, [analytics, marketing]);

  if (!visible || isOnboarding) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 inset-x-0 z-[100] p-4 sm:p-6"
        >
          <div className="mx-auto max-w-4xl rounded-2xl border-2 border-border bg-card/95 backdrop-blur-md shadow-2xl">
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex items-start gap-3 mb-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <Cookie className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground">
                    We value your privacy
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    We use cookies to enhance your experience, analyse site traffic, and personalise content.
                    By clicking &ldquo;Accept All&rdquo;, you consent to our use of cookies.
                    Read our{' '}
                    <a href="/privacy" className="text-primary hover:text-primary/80 underline underline-offset-2">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/gdpr" className="text-primary hover:text-primary/80 underline underline-offset-2">
                      GDPR Policy
                    </a>{' '}
                    for more information.
                  </p>
                </div>
              </div>

              {/* Preferences Panel */}
              <AnimatePresence>
                {showPreferences && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="mb-4 space-y-3 rounded-xl border border-border bg-muted/30 p-4">
                      {/* Essential */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-green-500" />
                          <div>
                            <p className="text-sm font-medium text-foreground">Essential Cookies</p>
                            <p className="text-xs text-muted-foreground">Required for the site to function. Cannot be disabled.</p>
                          </div>
                        </div>
                        <ToggleSwitch checked={true} disabled />
                      </div>

                      {/* Analytics */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Settings className="h-4 w-4 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium text-foreground">Analytics Cookies</p>
                            <p className="text-xs text-muted-foreground">Help us understand how visitors interact with the site.</p>
                          </div>
                        </div>
                        <ToggleSwitch checked={analytics} onChange={setAnalytics} />
                      </div>

                      {/* Marketing */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Settings className="h-4 w-4 text-primary" />
                          <div>
                            <p className="text-sm font-medium text-foreground">Marketing Cookies</p>
                            <p className="text-xs text-muted-foreground">Used to deliver personalised advertisements.</p>
                          </div>
                        </div>
                        <ToggleSwitch checked={marketing} onChange={setMarketing} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {showPreferences ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSavePreferences}
                  >
                    Save Preferences
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreferences(true)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    Manage Preferences
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- Toggle Switch (inline, no external Switch dependency) ---------- */

function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange?: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`
        relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        ${disabled ? 'cursor-not-allowed opacity-60' : ''}
        ${checked ? 'bg-primary' : 'bg-muted-foreground/30'}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0
          transition duration-200 ease-in-out
          ${checked ? 'translate-x-5' : 'translate-x-0.5'}
        `}
      />
    </button>
  );
}
